// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data, the My data tab's phase 4 and phase 3 panels
 * (docs/AYNI_DATA_INTAKE_API.md sections 12 and 15–19):
 *
 * - **Shared with me**: datasets other people shared with this account,
 *   read only, with who shared them and until when.
 * - **Share** (one dataset): invite someone by email, make a link (shown
 *   once, at most 30 days), see who has access and withdraw it; show it
 *   to the meeting this browser presents (decision O6, when the site has
 *   it on); publish it openly under CC BY 4.0 (an owner decision, when on).
 * - **Lab members** (the owner): roles of the people holding the lab's
 *   seats: member, viewer (view only), or hand on the owner's role.
 * - **Continuous sources**: an S3-compatible bucket we read on a schedule
 *   with a read-only key; their state, alerts, pause, resume, delete, and
 *   the form to add one.
 *
 * Risky actions go through `guarded` (step-up, `myDataStepUp.ts`). Every
 * value shown came from the site, other people or a customer's bucket:
 * text only (`myDataDom.ts`).
 */

import { plural, t, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { PALETTE_IDS } from '../services/colorScaleDisplay'
import { REGRID_METHODS, type JobChoices, type RegridMethod } from '../services/ownData'
import {
  addSource,
  changeRole,
  closeDatasetPublicly,
  createShare,
  deleteSource,
  listMembers,
  listShares,
  listSources,
  openDatasetPublicly,
  revokeShare,
  showToMeeting,
  sourceAction,
  stopShowingToMeeting,
  type DataFeatures,
  type DatasetView,
  type MemberView,
  type ShareView,
  type SharedDataset,
  type SourceView,
} from '../services/ownDataApi'
import { SHARE_INVITE_DEFAULT_DAYS, SHARE_LINK_DEFAULT_DAYS, type LabRole } from '../services/ownDataSharing'
import { SOURCE_MIN_POLL_MINUTES, parseSourceInput, type EndpointProblem, type SourceInputError } from '../services/ownDataSources'
import { presentingMeeting } from '../services/privateData'
import { regridName } from '../services/errorBudget'
import { button, h, keepingDrafts } from './myDataDom'
import { guarded } from './myDataStepUp'

export interface PanelContext {
  /** Where the step-up box is drawn (kept across redraws). */
  stepSlot: HTMLElement
  /** Say a route's error code in plain words. */
  errorText: (code: string) => string
  features: DataFeatures
  role: LabRole | null
  readOnly: boolean
}

const day = (ms: number): string => formatDate(new Date(ms), { dateStyle: 'medium' })

function head(title: string): HTMLElement {
  return h('div', { class: 'pl-tab-detail-head mydata-head' },
    h('button', { type: 'button', class: 'pl-tab-icon pl-tab-back mydata-back', 'aria-label': t('myData.back.aria') }, '‹'),
    h('h3', { class: 'pl-tab-detail-name mydata-title' }, title))
}

function notice(text: string | null, tone: 'info' | 'error'): HTMLElement | null {
  if (!text) return null
  return h('p', { class: `mydata-notice${tone === 'error' ? ' is-error' : ''}`, role: tone === 'error' ? 'alert' : 'status' }, text)
}

function daysSelect(id: string, options: number[], chosen: number, label: string): HTMLSelectElement {
  const sel = h('select', { id, class: 'mydata-input mydata-days', 'aria-label': label })
  for (const d of options) sel.append(h('option', { value: String(d), selected: d === chosen }, plural(d, { one: 'myData.share.days.one', other: 'myData.share.days.other' })))
  return sel
}

// ------------------------------------------------------------ Shared with me

/** The datasets shared with this account, each with Open. */
export function sharedSection(shared: readonly SharedDataset[]): HTMLElement {
  const box = h('section', { class: 'mydata-shared', 'aria-labelledby': 'mydata-shared-title' }, h('h4', { id: 'mydata-shared-title' }, t('myData.shared.title')))
  if (!shared.length) {
    box.append(h('p', { class: 'mydata-hint' }, t('myData.shared.empty')))
    return box
  }
  box.append(h('p', { class: 'mydata-hint' }, t('myData.shared.readOnly')))
  const ul = h('ul', { class: 'mydata-list', role: 'list' })
  for (const d of shared) {
    const meta = [
      d.sharedBy ? t('myData.shared.by', { who: d.sharedBy }) : null,
      t(d.via === 'link' ? 'myData.shared.viaLink' : 'myData.shared.viaInvite'),
      t('myData.shared.until', { date: day(d.expiresAt) }),
    ].filter(Boolean).join(' · ')
    ul.append(h('li', { class: 'mydata-row mydata-shared-row', 'data-id': d.id },
      h('div', { class: 'mydata-row-main' },
        h('span', { class: 'mydata-row-title' }, d.title),
        h('span', { class: 'mydata-chip', 'data-state': 'shared' }, t('myData.shared.chip'))),
      h('span', { class: 'mydata-row-meta' }, meta),
      h('div', { class: 'mydata-actions' },
        button('pl-tab-btn mydata-open-shared', t('myData.open'), { 'data-id': d.id, 'data-title': d.title, 'aria-label': t('myData.open.aria', { title: d.title }) }))))
  }
  box.append(ul)
  return box
}

// ---------------------------------------------------------------- Share

/** One dataset's sharing: invitations, links, who has access, meeting, open publishing. */
export function mountSharePanel(container: HTMLElement, ctx: PanelContext, dataset: DatasetView): { refresh: () => void } {
  let shares: ShareView[] | null = null
  let note: { text: string; tone: 'info' | 'error' } | null = null
  let freshLink: { url: string; until: number } | null = null
  let shownToMeeting = false
  /** Decision O6's condition: the presenter reads who will see it, how long, and that it is logged, and says yes. */
  let confirmMeeting = false
  let open = dataset.open ?? null
  let openUrl: string | null = null
  let busy = false
  /** Field ids to clear at the next redraw (sent on purpose). */
  let resetFields: string[] = []

  const load = async () => {
    const r = await listShares(dataset.id)
    if (r.ok) shares = r.value.shares
    else note = { text: ctx.errorText(r.error), tone: 'error' }
    render()
  }

  function shareRow(s: ShareView): HTMLElement {
    const who = s.kind === 'link' ? t('myData.share.linkName') : s.email ?? t('myData.share.someone')
    const status = s.state !== 'active'
      ? t(s.state === 'revoked' ? 'myData.share.revoked' : 'myData.share.expired')
      : s.kind === 'link'
        ? plural(s.accepted ?? 0, { one: 'myData.share.opened.one', other: 'myData.share.opened.other' })
        : t(s.seen ? 'myData.share.seen' : 'myData.share.notSeen')
    return h('li', { class: 'mydata-share-row', 'data-state': s.state },
      h('span', { class: 'mydata-row-title' }, who),
      h('span', { class: 'mydata-row-meta' }, [status, s.state === 'active' ? t('myData.shared.until', { date: day(s.expiresAt) }) : ''].filter(Boolean).join(' · ')),
      s.state === 'active' ? button('pl-tab-btn pl-tab-btn-quiet mydata-revoke', t('myData.share.revoke'), { 'data-share': s.id, 'aria-label': t('myData.share.revokeAria', { who }) }) : null)
  }

  function render(): void {
    const reset = resetFields
    resetFields = []
    keepingDrafts(container, () => draw(), reset)
  }

  function draw(): void {
    const email = h('input', { type: 'email', id: 'mydata-share-email', class: 'mydata-input', autocomplete: 'off', spellcheck: 'false', maxlength: 200 })
    const inviteDays = daysSelect('mydata-share-invite-days', [7, 30, 90, 365], SHARE_INVITE_DEFAULT_DAYS, t('myData.share.forHowLong'))
    const linkDays = daysSelect('mydata-share-link-days', [1, 7, 30], SHARE_LINK_DEFAULT_DAYS, t('myData.share.forHowLong'))
    const wrap = h('div', { class: 'mydata-share-view', 'data-dataset': dataset.id },
      head(t('myData.share.title', { title: dataset.title })),
      h('p', { class: 'mydata-intro' }, t('myData.share.intro')),
      notice(note?.text ?? null, note?.tone ?? 'info'),
      h('section', { class: 'mydata-share-block', 'aria-labelledby': 'mydata-share-invite-title' },
        h('h4', { id: 'mydata-share-invite-title' }, t('myData.share.inviteTitle')),
        h('label', { class: 'mydata-label', for: 'mydata-share-email' }, t('myData.share.emailLabel')),
        email,
        h('div', { class: 'mydata-pair' }, inviteDays, button('pl-tab-btn mydata-share-invite', t('myData.share.invite'))),
        h('p', { class: 'mydata-hint' }, t('myData.share.inviteHint'))),
      h('section', { class: 'mydata-share-block', 'aria-labelledby': 'mydata-share-link-title' },
        h('h4', { id: 'mydata-share-link-title' }, t('myData.share.linkTitle')),
        h('div', { class: 'mydata-pair' }, linkDays, button('pl-tab-btn mydata-share-link', t('myData.share.makeLink'))),
        freshLink
          ? h('div', { class: 'mydata-link-box' },
              h('label', { class: 'mydata-label', for: 'mydata-share-link-out' }, t('myData.share.linkOnce', { date: day(freshLink.until) })),
              h('div', { class: 'mydata-pair' },
                h('input', { type: 'text', id: 'mydata-share-link-out', class: 'mydata-input mydata-link-out', readOnly: true, value: freshLink.url, spellcheck: 'false' }),
                button('pl-tab-btn mydata-share-copy', t('myData.share.copy'))))
          : h('p', { class: 'mydata-hint' }, t('myData.share.linkHint'))))
    const list = h('section', { class: 'mydata-share-block', 'aria-labelledby': 'mydata-share-who-title' }, h('h4', { id: 'mydata-share-who-title' }, t('myData.share.whoTitle')))
    if (shares === null) list.append(h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading')))
    else if (!shares.length) list.append(h('p', { class: 'mydata-hint' }, t('myData.share.nobody')))
    else {
      const ul = h('ul', { class: 'mydata-share-list', role: 'list' })
      for (const s of shares) ul.append(shareRow(s))
      list.append(ul)
    }
    wrap.append(list)
    const meeting = presentingMeeting()
    if (ctx.features.meetingShares && meeting) {
      const ends = Number(meeting.st.split('.')[2])
      const until = Number.isFinite(ends) ? formatDate(new Date(ends), { timeStyle: 'short' }) : ''
      const block = h('section', { class: 'mydata-share-block', 'aria-labelledby': 'mydata-share-meeting-title' },
        h('h4', { id: 'mydata-share-meeting-title' }, t('myData.meeting.title')),
        h('p', { class: 'mydata-hint' }, t('myData.meeting.hint')))
      if (confirmMeeting && !shownToMeeting) {
        block.append(h('div', { class: 'mydata-cancel-confirm mydata-meeting-confirm', role: 'group', 'aria-label': t('myData.meeting.show') },
          h('p', { class: 'mydata-notice is-error' }, t('myData.meeting.notice', { title: dataset.title, until })),
          h('div', { class: 'mydata-actions' },
            button('pl-tab-btn mydata-meeting-yes', t('myData.meeting.yes')),
            button('pl-tab-btn pl-tab-btn-quiet mydata-meeting-keep', t('myData.meeting.keep')))))
      } else {
        block.append(button(`pl-tab-btn mydata-meeting-${shownToMeeting ? 'stop' : 'show'}`, t(shownToMeeting ? 'myData.meeting.stop' : 'myData.meeting.show')))
      }
      wrap.append(block)
    }
    if (ctx.features.openPublishing && ctx.role === 'owner') {
      const block = h('section', { class: 'mydata-share-block mydata-open-block', 'aria-labelledby': 'mydata-open-title' }, h('h4', { id: 'mydata-open-title' }, t('myData.openPub.title')))
      if (open) {
        block.append(h('p', { class: 'mydata-notice', role: 'status' }, t('myData.openPub.isOpen')),
          openUrl ? h('p', { class: 'mydata-hint mydata-open-url' }, openUrl) : '',
          h('div', { class: 'mydata-actions' }, button('pl-tab-btn pl-tab-btn-quiet mydata-open-withdraw', t('myData.openPub.withdraw'))))
      } else {
        const confirmInput = h('input', { type: 'text', id: 'mydata-open-confirm', class: 'mydata-input', autocomplete: 'off', spellcheck: 'false' })
        const go = button('pl-tab-btn mydata-open-publish', t('myData.openPub.publish'), { disabled: true })
        confirmInput.addEventListener('input', () => {
          go.disabled = confirmInput.value.trim() !== dataset.title.trim()
        })
        block.append(
          h('p', { class: 'mydata-notice is-error' }, t('myData.openPub.warning')),
          h('label', { class: 'mydata-label', for: 'mydata-open-confirm' }, t('myData.openPub.typeTitle')),
          confirmInput,
          h('div', { class: 'mydata-actions' }, go))
      }
      wrap.append(block)
    }
    container.replaceChildren(wrap)
  }

  container.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button')
    if (!btn || busy || !container.contains(btn)) return
    const act = async (fn: () => Promise<void>) => {
      busy = true
      btn.setAttribute('aria-busy', 'true')
      try {
        await fn()
      } finally {
        busy = false
        btn.removeAttribute('aria-busy')
      }
    }
    if (btn.classList.contains('mydata-share-invite')) {
      const email = container.querySelector<HTMLInputElement>('#mydata-share-email')?.value.trim() ?? ''
      const days = Number(container.querySelector<HTMLSelectElement>('#mydata-share-invite-days')?.value)
      if (!email) {
        note = { text: t('myData.share.noEmail'), tone: 'error' }
        return render()
      }
      void act(async () => {
        const r = await guarded(ctx.stepSlot, 'share.create', () => createShare(dataset.id, { kind: 'account', email, days }))
        if (!r.ok) note = r.error === 'step_up_cancelled' ? null : { text: ctx.errorText(r.error), tone: 'error' }
        else {
          note = { text: t(r.value.mailed ? 'myData.share.invited' : 'myData.share.invitedNoMail', { email }), tone: 'info' }
          resetFields = ['mydata-share-email']
        }
        await load()
      })
      return
    }
    if (btn.classList.contains('mydata-share-link')) {
      const days = Number(container.querySelector<HTMLSelectElement>('#mydata-share-link-days')?.value)
      void act(async () => {
        const r = await guarded(ctx.stepSlot, 'share.create', () => createShare(dataset.id, { kind: 'link', days }))
        if (!r.ok) note = r.error === 'step_up_cancelled' ? null : { text: ctx.errorText(r.error), tone: 'error' }
        else {
          freshLink = r.value.link ? { url: r.value.link, until: r.value.share.expiresAt } : null
          note = { text: t('myData.share.linkMade'), tone: 'info' }
        }
        await load()
        container.querySelector<HTMLInputElement>('#mydata-share-link-out')?.select()
      })
      return
    }
    if (btn.classList.contains('mydata-share-copy') && freshLink) {
      const url = freshLink.url
      void navigator.clipboard?.writeText(url).then(
        () => { note = { text: t('myData.share.copied'), tone: 'info' }; render() },
        () => { container.querySelector<HTMLInputElement>('#mydata-share-link-out')?.select() },
      )
      return
    }
    if (btn.classList.contains('mydata-revoke') && btn.dataset.share) {
      const id = btn.dataset.share
      void act(async () => {
        const r = await revokeShare(id)
        note = r.ok ? { text: t('myData.share.revokedDone'), tone: 'info' } : { text: ctx.errorText(r.error), tone: 'error' }
        await load()
      })
      return
    }
    if (btn.classList.contains('mydata-meeting-show')) {
      confirmMeeting = true
      render()
      container.querySelector<HTMLButtonElement>('.mydata-meeting-keep')?.focus()
      return
    }
    if (btn.classList.contains('mydata-meeting-keep')) {
      confirmMeeting = false
      return render()
    }
    if (btn.classList.contains('mydata-meeting-yes') || btn.classList.contains('mydata-meeting-stop')) {
      const m = presentingMeeting()
      if (!m) return
      const show = btn.classList.contains('mydata-meeting-yes')
      confirmMeeting = false
      void act(async () => {
        const r = show ? await guarded(ctx.stepSlot, 'meeting.share', () => showToMeeting(dataset.id, m.room, m.st)) : await stopShowingToMeeting(dataset.id, m.room)
        if (r.ok) {
          shownToMeeting = show
          note = { text: t(show ? 'myData.meeting.shown' : 'myData.meeting.stopped'), tone: 'info' }
        } else if (r.error !== 'step_up_cancelled') note = { text: ctx.errorText(r.error), tone: 'error' }
        render()
      })
      return
    }
    if (btn.classList.contains('mydata-open-publish')) {
      const confirm = container.querySelector<HTMLInputElement>('#mydata-open-confirm')?.value ?? ''
      void act(async () => {
        const r = await guarded(ctx.stepSlot, 'dataset.open', () => openDatasetPublicly(dataset.id, confirm))
        if (r.ok) {
          open = r.value.open
          openUrl = r.value.url
          note = { text: t('myData.openPub.done'), tone: 'info' }
        } else if (r.error !== 'step_up_cancelled') note = { text: ctx.errorText(r.error), tone: 'error' }
        render()
      })
      return
    }
    if (btn.classList.contains('mydata-open-withdraw')) {
      void act(async () => {
        const r = await closeDatasetPublicly(dataset.id)
        if (r.ok) {
          open = null
          openUrl = null
          note = { text: t('myData.openPub.withdrawn'), tone: 'info' }
        } else note = { text: ctx.errorText(r.error), tone: 'error' }
        render()
      })
    }
  })

  return {
    refresh: () => {
      render()
      void load()
    },
  }
}

// ---------------------------------------------------------------- Members

const ROLE_TEXT: Record<LabRole, MessageKey> = { owner: 'myData.role.owner', member: 'myData.role.member', viewer: 'myData.role.viewer' }

/** The lab's members and their roles (the owner). */
export function mountMembersPanel(container: HTMLElement, ctx: PanelContext): { refresh: () => void } {
  let members: MemberView[] | null = null
  let note: { text: string; tone: 'info' | 'error' } | null = null
  let busy = false

  const load = async () => {
    const r = await listMembers()
    if (r.ok) members = r.value.members
    else note = { text: ctx.errorText(r.error), tone: 'error' }
    render()
  }

  function render(): void {
    keepingDrafts(container, draw)
  }

  function draw(): void {
    const wrap = h('div', { class: 'mydata-members-view' },
      head(t('myData.members.title')),
      h('p', { class: 'mydata-intro' }, t('myData.members.intro')),
      h('dl', { class: 'mydata-facts mydata-roles-help' },
        h('dt', null, t('myData.role.owner')), h('dd', null, t('myData.role.ownerHelp')),
        h('dt', null, t('myData.role.member')), h('dd', null, t('myData.role.memberHelp')),
        h('dt', null, t('myData.role.viewer')), h('dd', null, t('myData.role.viewerHelp'))),
      notice(note?.text ?? null, note?.tone ?? 'info'))
    if (!members) {
      wrap.append(h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading')))
      container.replaceChildren(wrap)
      return
    }
    const ul = h('ul', { class: 'mydata-member-list', role: 'list' })
    for (const m of members) {
      const label = m.email ?? m.account
      const sel = h('select', { class: 'mydata-input mydata-role', 'data-account': m.account, 'aria-label': t('myData.members.roleOf', { who: label }), disabled: m.you || m.role === 'owner' })
      for (const r of ['member', 'viewer', 'owner'] as const) sel.append(h('option', { value: r, selected: r === m.role }, t(ROLE_TEXT[r])))
      ul.append(h('li', { class: 'mydata-member-row', 'data-account': m.account },
        h('span', { class: 'mydata-row-title' }, m.you ? t('myData.members.you', { who: label }) : label),
        h('span', { class: 'mydata-row-meta' }, m.lastSeenHolding ? t('myData.members.seen', { date: day(m.lastSeenHolding) }) : ''),
        h('div', { class: 'mydata-pair' }, sel, m.you || m.role === 'owner' ? null : button('pl-tab-btn pl-tab-btn-quiet mydata-role-save', t('myData.members.save'), { 'data-account': m.account }))))
    }
    wrap.append(ul)
    container.replaceChildren(wrap)
  }

  container.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button.mydata-role-save')
    if (!btn || busy || !btn.dataset.account) return
    const account = btn.dataset.account
    const role = container.querySelector<HTMLSelectElement>(`select.mydata-role[data-account="${CSS.escape(account)}"]`)?.value as LabRole | undefined
    if (!role) return
    busy = true
    void guarded(ctx.stepSlot, 'role.change', () => changeRole(account, role)).then(async (r) => {
      busy = false
      if (r.ok) note = { text: t(role === 'owner' ? 'myData.members.handedOn' : 'myData.members.saved'), tone: 'info' }
      else if (r.error !== 'step_up_cancelled') note = { text: ctx.errorText(r.error), tone: 'error' }
      await load()
    })
  })

  return { refresh: () => { render(); void load() } }
}

// ---------------------------------------------------------------- Sources

const SOURCE_STATE_TEXT: Record<string, MessageKey> = {
  testing: 'myData.source.state.testing',
  active: 'myData.source.state.active',
  paused: 'myData.source.state.paused',
  broken: 'myData.source.state.broken',
  refused: 'myData.source.state.refused',
}

const ALERT_TEXT: Record<string, MessageKey> = {
  drift: 'myData.source.alert.drift',
  credentials: 'myData.source.alert.credentials',
  writable: 'myData.source.alert.writable',
  unreachable: 'myData.source.alert.unreachable',
  missed: 'myData.source.alert.missed',
  unreadable: 'myData.source.alert.unreadable',
  quota: 'myData.source.alert.quota',
  variable_missing: 'myData.source.alert.variableMissing',
  release_refused: 'myData.source.alert.refused',
}

const ENDPOINT_TEXT: Record<EndpointProblem, MessageKey> = {
  not_url: 'myData.source.bad.endpoint',
  not_https: 'myData.source.bad.https',
  credentials_in_url: 'myData.source.bad.credentialsInUrl',
  path_not_allowed: 'myData.source.bad.path',
  bad_host: 'myData.source.bad.endpoint',
  internal_host: 'myData.source.bad.internal',
  private_address: 'myData.source.bad.internal',
  bad_port: 'myData.source.bad.port',
}

const FIELD_TEXT: Record<Exclude<SourceInputError['field'], 'endpoint'>, MessageKey> = {
  title: 'myData.source.bad.title',
  bucket: 'myData.source.bad.bucket',
  prefix: 'myData.source.bad.prefix',
  region: 'myData.source.bad.region',
  accessKeyId: 'myData.source.bad.keyId',
  secretAccessKey: 'myData.source.bad.secret',
  schedule: 'myData.source.bad.schedule',
  window: 'myData.source.bad.window',
  choices: 'myData.source.bad.choices',
}

/** A source's input problem in plain words. */
export function sourceProblemText(e: SourceInputError): string {
  return t(e.field === 'endpoint' ? ENDPOINT_TEXT[e.problem] : FIELD_TEXT[e.field])
}

/** Continuous sources: the list with alerts, and the form to add one. */
export function mountSourcesPanel(container: HTMLElement, ctx: PanelContext): { refresh: () => void } {
  let sources: SourceView[] | null = null
  let quota: { sources: number; used: number } | null = null
  let note: { text: string; tone: 'info' | 'error' } | null = null
  let adding = false
  let confirmDelete: string | null = null
  let busy = false
  const canManage = !ctx.readOnly && (ctx.role === 'owner' || ctx.role === 'member')

  const load = async () => {
    const r = await listSources()
    if (r.ok) {
      sources = r.value.sources
      quota = r.value.quota
    } else note = { text: ctx.errorText(r.error), tone: 'error' }
    render()
  }

  function card(s: SourceView): HTMLElement {
    const schedule = s.schedule.kind === 'poll' ? t('myData.source.every', { n: formatNumber(s.schedule.minutes) }) : t('myData.source.notify')
    const window = s.window.kind === 'recent' ? t('myData.source.recent', { n: formatNumber(s.window.frames) }) : t('myData.source.archive')
    const mine = s.mine || ctx.role === 'owner'
    const alerts = h('ul', { class: 'mydata-alerts', role: 'list' })
    for (const a of s.alerts) {
      alerts.append(h('li', { class: 'mydata-alert', 'data-kind': a.kind },
        h('span', { class: 'mydata-alert-kind' }, t(ALERT_TEXT[a.kind] ?? 'myData.source.alert.refused')),
        h('span', { class: 'mydata-row-meta' }, [formatDate(new Date(a.at), { dateStyle: 'medium', timeStyle: 'short' }), a.releaseKept ? t('myData.source.kept', { release: a.releaseKept }) : ''].filter(Boolean).join(' · ')),
        a.message ? h('span', { class: 'mydata-alert-message' }, a.message) : null))
    }
    return h('li', { class: 'mydata-source', 'data-id': s.id, 'data-state': s.state },
      h('div', { class: 'mydata-row-main' },
        h('span', { class: 'mydata-row-title' }, s.title),
        h('span', { class: 'mydata-chip', 'data-state': s.state }, t(SOURCE_STATE_TEXT[s.state] ?? 'myData.source.state.broken'))),
      h('span', { class: 'mydata-row-meta mydata-source-where' }, `${s.endpoint} · ${s.bucket}/${s.prefix}${s.keyHint ? ` · ${t('myData.source.keyHint', { hint: s.keyHint })}` : ''}`),
      h('span', { class: 'mydata-row-meta' }, [schedule, window, s.lastPollAt ? t('myData.source.lastPoll', { date: formatDate(new Date(s.lastPollAt), { dateStyle: 'medium', timeStyle: 'short' }) }) : t('myData.source.notPolled')].join(' · ')),
      s.lastGoodRelease ? h('span', { class: 'mydata-row-meta' }, t('myData.source.lastGood', { release: s.lastGoodRelease })) : null,
      s.alerts.length ? alerts : null,
      mine && canManage
        ? confirmDelete === s.id
          ? h('div', { class: 'mydata-cancel-confirm', role: 'group' },
              h('p', null, t('myData.source.deleteConfirm')),
              h('div', { class: 'mydata-actions' },
                button('pl-tab-btn mydata-source-delete-yes', t('myData.source.deleteYes'), { 'data-id': s.id }),
                button('pl-tab-btn pl-tab-btn-quiet mydata-source-delete-keep', t('myData.source.deleteKeep'))))
          : h('div', { class: 'mydata-actions' },
              s.state === 'active' || s.state === 'broken' ? button('pl-tab-btn pl-tab-btn-quiet mydata-source-pause', t('myData.source.pause'), { 'data-id': s.id }) : null,
              s.state === 'paused' ? button('pl-tab-btn pl-tab-btn-quiet mydata-source-resume', t('myData.source.resume'), { 'data-id': s.id }) : null,
              s.alerts.length ? button('pl-tab-btn pl-tab-btn-quiet mydata-source-clear', t('myData.source.clear'), { 'data-id': s.id }) : null,
              button('pl-tab-btn pl-tab-btn-quiet mydata-source-delete', t('myData.source.delete'), { 'data-id': s.id, 'aria-label': t('myData.source.deleteAria', { title: s.title }) }))
        : null)
  }

  function form(): HTMLElement {
    const f = h('form', { class: 'mydata-source-form', novalidate: true, 'aria-label': t('myData.source.addTitle') }, h('h4', null, t('myData.source.addTitle')))
    const field = (id: string, label: string, control: HTMLElement, hint?: string) =>
      h('div', { class: 'mydata-field' }, h('label', { class: 'mydata-label', for: id }, label), control, hint ? h('p', { class: 'mydata-hint' }, hint) : null)
    const input = (id: string, props: Record<string, string | number | boolean> = {}) => h('input', { type: 'text', id, class: 'mydata-input', autocomplete: 'off', spellcheck: 'false', ...props })
    f.append(
      h('p', { class: 'mydata-hint' }, t('myData.source.intro')),
      field('mydata-src-title', t('myData.source.titleLabel'), input('mydata-src-title', { maxlength: 200 })),
      field('mydata-src-endpoint', t('myData.source.endpoint'), input('mydata-src-endpoint', { type: 'url', placeholder: 'https://s3.eu-central-1.amazonaws.com' }), t('myData.source.endpointHint')),
      field('mydata-src-bucket', t('myData.source.bucket'), input('mydata-src-bucket', { maxlength: 63 })),
      field('mydata-src-prefix', t('myData.source.prefix'), input('mydata-src-prefix', { maxlength: 512 }), t('myData.source.prefixHint')),
      field('mydata-src-region', t('myData.source.region'), input('mydata-src-region', { value: 'auto', maxlength: 32 })),
      field('mydata-src-key', t('myData.source.keyId'), input('mydata-src-key', { maxlength: 128 })),
      field('mydata-src-secret', t('myData.source.secret'), input('mydata-src-secret', { type: 'password', autocomplete: 'new-password', maxlength: 256 }), t('myData.source.secretHint')),
      h('fieldset', { class: 'mydata-field' }, h('legend', { class: 'mydata-label' }, t('myData.source.schedule')),
        h('label', { class: 'mydata-check', for: 'mydata-src-poll' }, h('input', { type: 'radio', name: 'schedule', id: 'mydata-src-poll', value: 'poll', checked: true }), t('myData.source.pollEvery')),
        h('input', { type: 'number', id: 'mydata-src-minutes', class: 'mydata-input mydata-num', min: SOURCE_MIN_POLL_MINUTES, step: 1, value: 15, 'aria-label': t('myData.source.minutes') }),
        h('label', { class: 'mydata-check', for: 'mydata-src-notify' }, h('input', { type: 'radio', name: 'schedule', id: 'mydata-src-notify', value: 'notify' }), t('myData.source.notifyLabel')),
        h('p', { class: 'mydata-hint' }, t('myData.source.scheduleHint'))),
      h('fieldset', { class: 'mydata-field' }, h('legend', { class: 'mydata-label' }, t('myData.source.window')),
        h('label', { class: 'mydata-check', for: 'mydata-src-recent' }, h('input', { type: 'radio', name: 'window', id: 'mydata-src-recent', value: 'recent', checked: true }), t('myData.source.recentLabel')),
        h('input', { type: 'number', id: 'mydata-src-frames', class: 'mydata-input mydata-num', min: 1, step: 1, value: 120, 'aria-label': t('myData.source.frames') }),
        h('label', { class: 'mydata-check', for: 'mydata-src-archive' }, h('input', { type: 'radio', name: 'window', id: 'mydata-src-archive', value: 'archive' }), t('myData.source.archiveLabel'))),
      field('mydata-src-variable', t('myData.source.variable'), input('mydata-src-variable', { maxlength: 128 }), t('myData.source.variableHint')))
    const palette = h('select', { id: 'mydata-src-palette', class: 'mydata-input' })
    for (const id of PALETTE_IDS) if (id !== 'source') palette.append(h('option', { value: id, selected: id === 'viridis' }, t(`colorbar.palette.${id}` as MessageKey)))
    const regrid = h('select', { id: 'mydata-src-regrid', class: 'mydata-input' })
    for (const m of REGRID_METHODS) regrid.append(h('option', { value: m, selected: m === 'bilinear' }, regridName(m)))
    f.append(
      field('mydata-src-palette', t('myData.choices.palette'), palette),
      field('mydata-src-regrid', t('myData.choices.regrid'), regrid),
      h('div', { class: 'mydata-field' },
        h('label', { class: 'mydata-label', for: 'mydata-src-tol' }, t('myData.source.tolerance')),
        h('input', { type: 'number', id: 'mydata-src-tol', class: 'mydata-input mydata-num', step: 'any', min: 0 }),
        h('p', { class: 'mydata-hint' }, t('myData.source.toleranceHint'))),
      h('p', { class: 'mydata-notice is-error mydata-src-error', role: 'alert', hidden: true }),
      h('div', { class: 'mydata-actions' },
        h('button', { type: 'submit', class: 'pl-tab-btn mydata-src-submit' }, t('myData.source.add')),
        button('pl-tab-btn pl-tab-btn-quiet mydata-src-cancel', t('myData.source.cancel'))))
    f.addEventListener('submit', (e) => {
      e.preventDefault()
      if (busy) return
      const val = (id: string) => f.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? ''
      const checked = (name: string) => f.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`)?.value
      const tol = Number(val('mydata-src-tol'))
      const choices: JobChoices = { variable: val('mydata-src-variable'), palette: palette.value, regrid: regrid.value as RegridMethod, ...(val('mydata-src-tol') && tol > 0 ? { tolerance: { quantile: 0.99, abs: tol } } : {}) }
      const body = {
        title: val('mydata-src-title'),
        endpoint: val('mydata-src-endpoint'),
        bucket: val('mydata-src-bucket'),
        prefix: val('mydata-src-prefix'),
        region: val('mydata-src-region') || 'auto',
        accessKeyId: val('mydata-src-key'),
        secretAccessKey: f.querySelector<HTMLInputElement>('#mydata-src-secret')?.value ?? '',
        schedule: checked('schedule') === 'notify' ? { kind: 'notify' as const } : { kind: 'poll' as const, minutes: Number(val('mydata-src-minutes')) },
        window: checked('window') === 'archive' ? { kind: 'archive' as const } : { kind: 'recent' as const, frames: Number(val('mydata-src-frames')) },
        choices,
      }
      const errBox = f.querySelector<HTMLElement>('.mydata-src-error')!
      const parsed = parseSourceInput(body)
      if (!parsed.ok) {
        errBox.textContent = sourceProblemText(parsed.error)
        errBox.hidden = false
        return
      }
      busy = true
      void guarded(ctx.stepSlot, 'source.add', () => addSource(parsed.value)).then(async (r) => {
        busy = false
        if (!r.ok) {
          if (r.error === 'step_up_cancelled') return
          const b = r.body as { field?: string; problem?: string } | undefined
          errBox.textContent = r.error === 'bad_source' && b?.field ? sourceProblemText({ field: b.field, problem: b.problem } as SourceInputError) : ctx.errorText(r.error)
          errBox.hidden = false
          return
        }
        adding = false
        note = { text: t(r.value.dispatched ? 'myData.source.added' : 'myData.source.addedQueued'), tone: 'info' }
        await load()
      })
    })
    return f
  }

  function render(): void {
    keepingDrafts(container, draw)
  }

  function draw(): void {
    const full = quota !== null && quota.used >= quota.sources
    const wrap = h('div', { class: 'mydata-sources-view' },
      head(t('myData.source.title')),
      h('p', { class: 'mydata-intro' }, t('myData.source.panelIntro')),
      quota ? h('p', { class: 'mydata-quota-text' }, t('myData.source.quota', { used: formatNumber(quota.used), total: formatNumber(quota.sources) })) : null,
      notice(note?.text ?? null, note?.tone ?? 'info'))
    if (adding) wrap.append(form())
    else if (canManage) {
      wrap.append(h('div', { class: 'mydata-actions' }, button('pl-tab-btn mydata-source-add', t('myData.source.addOpen'), { disabled: full })),
        full ? h('p', { class: 'mydata-hint' }, t('myData.source.full')) : '')
    }
    if (!sources) wrap.append(h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading')))
    else if (!sources.length) wrap.append(h('p', { class: 'pl-tab-empty' }, t('myData.source.none')))
    else {
      const ul = h('ul', { class: 'mydata-source-list', role: 'list' })
      for (const s of sources) ul.append(card(s))
      wrap.append(ul)
    }
    container.replaceChildren(wrap)
  }

  container.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button')
    if (!btn || busy || !container.contains(btn)) return
    if (btn.classList.contains('mydata-source-add')) {
      adding = true
      note = null
      render()
      container.querySelector<HTMLInputElement>('#mydata-src-title')?.focus()
      return
    }
    if (btn.classList.contains('mydata-src-cancel')) {
      adding = false
      return render()
    }
    if (btn.classList.contains('mydata-source-delete') && btn.dataset.id) {
      confirmDelete = btn.dataset.id
      render()
      container.querySelector<HTMLButtonElement>('.mydata-source-delete-keep')?.focus()
      return
    }
    if (btn.classList.contains('mydata-source-delete-keep')) {
      confirmDelete = null
      return render()
    }
    const id = btn.dataset.id
    if (!id) return
    const run = (p: Promise<{ ok: boolean; error?: string }>, done: MessageKey) => {
      busy = true
      void p.then(async (r) => {
        busy = false
        confirmDelete = null
        note = r.ok ? { text: t(done), tone: 'info' } : { text: ctx.errorText(r.error ?? 'generic'), tone: 'error' }
        await load()
      })
    }
    if (btn.classList.contains('mydata-source-delete-yes')) run(deleteSource(id) as Promise<{ ok: boolean; error?: string }>, 'myData.source.deleted')
    else if (btn.classList.contains('mydata-source-pause')) run(sourceAction(id, 'pause') as Promise<{ ok: boolean; error?: string }>, 'myData.source.paused')
    else if (btn.classList.contains('mydata-source-resume')) run(sourceAction(id, 'resume') as Promise<{ ok: boolean; error?: string }>, 'myData.source.resumed')
    else if (btn.classList.contains('mydata-source-clear')) run(sourceAction(id, 'clear-alerts') as Promise<{ ok: boolean; error?: string }>, 'myData.source.cleared')
  })

  return { refresh: () => { render(); void load() } }
}
